/* eslint-disable */ // v2
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { isAdminUser } from '../userUtils';

const MONTHS   = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const QUARTERS = [
  { label: 'Q1', num: 1, months: [1,2,3] },
  { label: 'Q2', num: 2, months: [4,5,6] },
  { label: 'Q3', num: 3, months: [7,8,9] },
  { label: 'Q4', num: 4, months: [10,11,12] },
];
const CURRENT_YEAR = new Date().getFullYear();
const YEARS        = [CURRENT_YEAR, CURRENT_YEAR - 1, CURRENT_YEAR - 2];
const VENUES       = ['Box Jordaan', 'Box Plantage', 'Sassys'];

const LOG_CATEGORY_LABELS = {
  capex:          '💰 Capex / Asset Purchase',
  board_decision: '🗳️ Board Decision',
  legal:          '⚖️ Legal / Compliance',
  personnel:      '👥 Personnel Change',
  regulatory:     '📋 Regulatory / Permit',
  distribution:   '💸 Distribution / Dividend',
  other:          '📝 Other',
};

const fmt    = (n) => (n !== null && n !== undefined && n !== 0) ? `€${Number(n).toLocaleString('en-US', { minimumFractionDigits:0, maximumFractionDigits:0 })}` : '—';
const fmtNum = (n) => (n !== null && n !== undefined && n !== 0) ? Number(n).toLocaleString('en-US') : '—';
const fmtPct = (cost, rev) => (cost && rev) ? ((cost / rev) * 100).toFixed(1) + '%' : '—';

const C = {
  charcoal:'#2F3A42', navy:'#445366', mid:'#7A8A96', border:'#D8DEE3',
  cream:'#F7F6EF', green:'#7EA594', greenLight:'#EAF2EF',
  yellow:'#F7EC71', yellowLight:'#FEFCE8', amber:'#E07B00', amberLight:'#FFF3E0',
  white:'#FFFFFF', red:'#DC3545', redLight:'#FFF0F0',
  skyBlue:'#BCD9E5', skyText:'#3A8FA3', skyLight:'#EAF4F8',
  lm:'#5B8A6F', lmLight:'#E8F2ED',
};

// Revenue from results_daily for a given month — used across components
function govDailyRev(dailyRows, monthNum) {
  return (dailyRows || [])
    .filter(d => new Date(d.date).getMonth()+1 === monthNum && (d.total_revenue||0) > 0)
    .reduce((s,d) => s+(d.total_revenue||0), 0) || null;
}

// Most recent day of month with revenue > 0 (for pro-rating costs)
function latestDayOfMonthGov(dailyRows, monthNum) {
  const rows = (dailyRows || []).filter(d => new Date(d.date).getMonth()+1 === monthNum && (d.total_revenue||0) > 0);
  if (!rows.length) return null;
  return new Date(rows.map(d => d.date).sort().reverse()[0]).getDate();
}

function calcEBITDA(r, isSassys = false) {
  if (!r) return 0;
  return (r.actual_revenue         || 0)
    - (r.food_cost                 || 0)
    - (isSassys ? 0 : (r.bev_cost || 0))
    - (isSassys ? (
        (r.oedipus_rent            || 0) +
        (r.oedipus_utilities       || 0) +
        (r.oedipus_rev_share       || 0)
      ) : 0)
    - (r.kitchen_payroll           || 0)
    - (isSassys ? 0 : (r.foh_payroll || 0))
    - (r.lm_prep_payroll_alloc     || 0)
    - (r.lm_transfers_alloc        || 0)
    - (r.group_costs               || 0)
    - (r.other_opex                || 0);
}

function PLRow({ label, actual, ly, indent=false, bold=false, highlight=false, isRevenue=false, dimIfZero=false, isMobile=false }) {
  const vsLY    = actual && ly ? actual - ly : null;
  const vsLYPct = actual && ly ? ((actual - ly) / Math.abs(ly) * 100) : null;
  const lyGood  = vsLY !== null && (isRevenue ? vsLY >= 0 : vsLY <= 0);
  if (dimIfZero && (!actual || actual === 0)) return null;
  const p = isMobile ? '7px 6px' : '9px 16px';
  const pl = (!isMobile && indent) ? '28px' : (isMobile ? '6px' : '16px');
  return (
    <tr style={{ borderBottom:`1px solid ${C.border}`, background: highlight ? C.yellowLight : 'transparent' }}>
      <td style={{ padding:p, paddingLeft:pl, fontSize:isMobile?'11px':'13px', fontWeight:bold?700:400, color:C.charcoal }}>{label}</td>
      <td style={{ padding:p, textAlign:'right', fontWeight:bold?800:600, fontSize:isMobile?'11px':'13px', color:C.charcoal }}>{actual ? fmt(actual) : '—'}</td>
      <td style={{ padding:p, textAlign:'right', fontSize:isMobile?'10px':'12px', color:C.mid }}>{ly ? fmt(ly) : '—'}</td>
      <td style={{ padding:p, textAlign:'right', fontSize:isMobile?'10px':'12px' }}>
        {vsLYPct !== null
          ? <span style={{ fontWeight:700, color:lyGood?C.green:C.red }}>{vsLYPct >= 0 ? '+' : ''}{vsLYPct.toFixed(1)}%</span>
          : '—'}
      </td>
    </tr>
  );
}

function ShareholderRegistryPanel({ venue, quarter, year, qRecord, onPublishToggle, publishing, currentUser, isAdvisor }) {
  const [shareholders, setShareholders] = useState([]);
  const [stakes, setStakes]             = useState([]);
  const [linkRecord, setLinkRecord]     = useState(null);
  const [loadingData, setLoadingData]   = useState(true);
  const [copied, setCopied]             = useState(false);
  const [sending, setSending]           = useState({});
  const [sendResult, setSendResult]     = useState({});
  const [generatingLink, setGeneratingLink] = useState(false);
  const [sentMap, setSentMap]           = useState({}); // shareholder_id → sent_at string

  // Add shareholder form
  const [showAddForm, setShowAddForm]   = useState(false);
  const [newName, setNewName]           = useState('');
  const [newEmail, setNewEmail]         = useState('');
  const [newPct, setNewPct]             = useState('');
  const [newEntity, setNewEntity]       = useState('');
  const [saving, setSaving]             = useState(false);

  const isPublished = qRecord?.published || false;
  const shareUrl    = linkRecord
    ? `${window.location.origin}/governance/share?token=${linkRecord.token}`
    : null;

  useEffect(() => { loadData(); }, [venue]);

  async function loadData() {
    setLoadingData(true);
    const [{ data: sData }, { data: stData }, { data: lData }, { data: sendData }] = await Promise.all([
      supabase.from('shareholders').select('*').eq('active', true).order('name'),
      supabase.from('shareholder_venue_stakes').select('*').eq('venue', venue),
      supabase.from('shareholder_links').select('*').eq('venue', venue).eq('quarter', quarter).eq('year', year).maybeSingle(),
      supabase.from('shareholder_report_sends').select('*').eq('venue', venue).eq('quarter', quarter).eq('year', year),
    ]);
    setShareholders(sData || []);
    setStakes(stData || []);
    setLinkRecord(lData || null);
    // Build sentMap: { shareholder_id: sent_at }
    const map = {};
    (sendData || []).forEach(s => { map[s.shareholder_id] = s.sent_at; });
    setSentMap(map);
    setLoadingData(false);
  }

  const venueStakes = stakes.filter(s => s.venue === venue);
  const venueShareholders = venueStakes.map(vs => ({
    ...vs,
    shareholder: shareholders.find(s => s.id === vs.shareholder_id),
  })).filter(vs => vs.shareholder).sort((a, b) => b.shareholding_pct - a.shareholding_pct);

  async function handleGenerateLink() {
    setGeneratingLink(true);
    const { data, error } = await supabase
      .from('shareholder_links')
      .upsert({
        venue, quarter, year,
        created_by: currentUser?.name || currentUser?.email,
        created_at: new Date().toISOString(),
      }, { onConflict: 'venue,quarter,year' })
      .select()
      .single();
    if (data) setLinkRecord(data);
    setGeneratingLink(false);
  }

  function handleCopy() {
    if (!shareUrl) return;
    navigator.clipboard.writeText(shareUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });
  }

  async function handleSendEmail(vs) {
    if (!shareUrl || !vs.shareholder?.email) return;
    const key = vs.shareholder_id;
    setSending(p => ({ ...p, [key]: true }));
    try {
      const res = await fetch(
        `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/send-shareholder-report`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({
            to:        vs.shareholder.email,
            name:      vs.shareholder.name,
            venue,
            quarter,
            year,
            reportUrl: shareUrl,
            sentBy:    currentUser?.name || currentUser?.email,
          }),
        }
      );
      const json = await res.json();
      if (json.ok) {
        // Record the send
        const now = new Date().toISOString();
        await supabase.from('shareholder_report_sends').upsert({
          shareholder_id: key,
          venue, quarter, year,
          sent_at: now,
        }, { onConflict: 'shareholder_id,venue,quarter,year' });
        setSentMap(p => ({ ...p, [key]: now }));
        setSendResult(p => ({ ...p, [key]: 'sent' }));
      } else {
        setSendResult(p => ({ ...p, [key]: 'error' }));
      }
      setTimeout(() => setSendResult(p => ({ ...p, [key]: null })), 4000);
    } catch {
      setSendResult(p => ({ ...p, [key]: 'error' }));
      setTimeout(() => setSendResult(p => ({ ...p, [key]: null })), 4000);
    }
    setSending(p => ({ ...p, [key]: false }));
  }

  async function handleAddShareholder() {
    if (!newName.trim()) return;
    setSaving(true);
    const { data: sh } = await supabase
      .from('shareholders')
      .insert([{ name: newName.trim(), email: newEmail.trim(), active: true }])
      .select().single();
    if (sh) {
      await supabase.from('shareholder_venue_stakes').insert([{
        shareholder_id: sh.id,
        venue,
        shareholding_pct: parseFloat(newPct) || 0,
        entity_name: newEntity.trim() || null,
      }]);
    }
    setNewName(''); setNewEmail(''); setNewPct(''); setNewEntity('');
    setShowAddForm(false);
    setSaving(false);
    loadData();
  }

  async function handleUpdateEmail(shareholderId, email) {
    await supabase.from('shareholders').update({ email }).eq('id', shareholderId);
    setShareholders(p => p.map(s => s.id === shareholderId ? { ...s, email } : s));
  }

  const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : null;

  // Summary counts for the header strip
  const totalShareholders = venueShareholders.length;
  const sentCount  = venueShareholders.filter(vs => sentMap[vs.shareholder_id]).length;
  const readCount  = linkRecord?.read_at ? totalShareholders : 0; // read_at on link = at least one read

  if (loadingData) return <div style={{ fontSize:12, color:C.mid, padding:'16px 0' }}>Loading shareholders…</div>;

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:16, minWidth:0, overflow:'hidden' }}>

      {/* Publish toggle */}
      <div style={{ border:`2px solid ${isPublished?C.green:C.border}`, borderRadius:12, background:isPublished?C.greenLight:C.white, padding:'18px 20px' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
          <div style={{ fontWeight:800, fontSize:14, color:C.charcoal }}>Shareholder Report</div>
          <span style={{ padding:'3px 10px', borderRadius:20, fontSize:11, fontWeight:700, background:isPublished?C.green:C.cream, color:isPublished?C.white:C.mid, border:`1px solid ${isPublished?C.green:C.border}` }}>
            {isPublished ? '● Live' : '○ Draft'}
          </span>
        </div>
        {isPublished && qRecord?.published_at && (
          <div style={{ fontSize:11, color:C.green, marginBottom:10, fontWeight:600 }}>
            Published {new Date(qRecord.published_at).toLocaleDateString('en-GB')}
            {qRecord.published_by ? ` by ${qRecord.published_by}` : ''}
          </div>
        )}
        {!isAdvisor && (
          <button onClick={onPublishToggle} disabled={publishing} style={{
            width:'100%', padding:10, background:publishing?C.border:(isPublished?C.red:C.green),
            color:C.white, border:'none', borderRadius:8, cursor:publishing?'not-allowed':'pointer',
            fontSize:13, fontWeight:700, opacity:publishing?0.6:1,
          }}>
            {publishing ? '…' : isPublished ? '↩ Unpublish' : `↑ Publish Q${quarter} ${year} to Shareholders`}
          </button>
        )}
      </div>

      {/* Share link */}
      <div style={{ background:C.white, border:`1.5px solid ${C.border}`, borderRadius:12, padding:'18px 20px' }}>
        <div style={{ fontWeight:700, fontSize:13, color:C.charcoal, marginBottom:4 }}>Shareholder Link</div>
        <div style={{ fontSize:11, color:C.mid, marginBottom:12 }}>One shared link for all shareholders this quarter. Publish first, then generate.</div>

        {!linkRecord ? (
          !isAdvisor && (
            <button onClick={handleGenerateLink} disabled={!isPublished || generatingLink} style={{
              width:'100%', padding:10, background:isPublished?C.charcoal:C.border,
              color:C.white, border:'none', borderRadius:8,
              cursor:isPublished?'pointer':'not-allowed', fontSize:13, fontWeight:700,
            }}>
              {generatingLink ? 'Generating…' : '🔗 Generate Link'}
            </button>
          )
        ) : (
          <div>
            <div style={{ display:'flex', gap:8, alignItems:'center', padding:'8px 12px', background:'#F8FAFC', border:`1px solid ${C.border}`, borderRadius:8, marginBottom:8 }}>
              <code style={{ fontSize:10, color:C.mid, flex:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{shareUrl}</code>
              {!isAdvisor && (
                <button onClick={handleCopy} style={{ padding:'4px 12px', background:copied?C.green:C.charcoal, color:C.white, border:'none', borderRadius:6, cursor:'pointer', fontSize:11, fontWeight:700, flexShrink:0 }}>
                  {copied ? '✓ Copied' : 'Copy'}
                </button>
              )}
            </div>
            {/* Read receipt on the link */}
            {linkRecord.read_at ? (
              <div style={{ fontSize:11, color:C.green, fontWeight:600, marginBottom:4 }}>
                ✓ Report opened {fmtDate(linkRecord.read_at)}
              </div>
            ) : (
              <div style={{ fontSize:11, color:C.mid, marginBottom:4 }}>No read confirmation yet</div>
            )}
            {!isAdvisor && (
              <button onClick={handleGenerateLink} disabled={generatingLink} style={{ fontSize:11, color:C.mid, background:'none', border:'none', cursor:'pointer', padding:0, textDecoration:'underline' }}>
                Regenerate link
              </button>
            )}
          </div>
        )}
      </div>

      {/* Shareholder list */}
      <div style={{ background:C.white, border:`1.5px solid ${C.border}`, borderRadius:12, padding:'18px 20px' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
          <div>
            <div style={{ fontWeight:700, fontSize:13, color:C.charcoal }}>Shareholders — {venue}</div>
            <div style={{ fontSize:11, color:C.mid, marginTop:2 }}>
              {totalShareholders} shareholders · {venueShareholders.reduce((a, s) => a + parseFloat(s.shareholding_pct), 0).toFixed(2)}% total
              {totalShareholders > 0 && (
                <span style={{ marginLeft:10 }}>
                  <span style={{ color: sentCount === totalShareholders ? C.green : C.amber, fontWeight:600 }}>
                    {sentCount}/{totalShareholders} sent
                  </span>
                </span>
              )}
            </div>
          </div>
          {!isAdvisor && (
            <button onClick={() => setShowAddForm(p => !p)} style={{ padding:'5px 12px', background:C.charcoal, color:C.white, border:'none', borderRadius:8, cursor:'pointer', fontSize:12, fontWeight:700 }}>
              {showAddForm ? 'Cancel' : '+ Add'}
            </button>
          )}
        </div>

        {/* Add form */}
        {showAddForm && (
          <div style={{ background:C.cream, borderRadius:10, padding:'14px 16px', marginBottom:14, border:`1px solid ${C.border}` }}>
            <div style={{ fontSize:12, fontWeight:700, color:C.charcoal, marginBottom:10 }}>Add Shareholder to {venue}</div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, marginBottom:8 }}>
              <input value={newName}   onChange={e=>setNewName(e.target.value)}   placeholder="Full name *" style={inputStyle} />
              <input value={newEmail}  onChange={e=>setNewEmail(e.target.value)}  placeholder="Email" type="email" style={inputStyle} />
              <input value={newPct}    onChange={e=>setNewPct(e.target.value)}    placeholder="Shareholding %" type="number" style={inputStyle} />
              <input value={newEntity} onChange={e=>setNewEntity(e.target.value)} placeholder="Entity name (optional)" style={inputStyle} />
            </div>
            <button onClick={handleAddShareholder} disabled={saving || !newName.trim()} style={{ padding:'7px 16px', background:C.green, color:C.white, border:'none', borderRadius:8, cursor:'pointer', fontSize:12, fontWeight:700 }}>
              {saving ? 'Saving…' : 'Add Shareholder'}
            </button>
          </div>
        )}

        {/* Shareholder rows */}
        {venueShareholders.length === 0 ? (
          <div style={{ fontSize:12, color:C.mid, textAlign:'center', padding:'16px 0' }}>No shareholders added yet for {venue}.</div>
        ) : venueShareholders.map(vs => {
          const sh      = vs.shareholder;
          const key     = vs.shareholder_id;
          const res     = sendResult[key];
          const sentAt  = sentMap[key];
          return (
            <div key={key} style={{ borderBottom:`1px solid ${C.border}`, paddingBottom:12, marginBottom:12 }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:6 }}>
                <div>
                  <div style={{ fontSize:13, fontWeight:700, color:C.charcoal }}>{sh.name}</div>
                  {vs.entity_name && <div style={{ fontSize:11, color:C.mid }}>{vs.entity_name}</div>}
                </div>
                <div style={{ fontSize:14, fontWeight:800, color:C.navy, flexShrink:0, marginLeft:8 }}>{vs.shareholding_pct}%</div>
              </div>

              {isAdvisor ? (
                <div style={{ fontSize:12, color:C.mid }}>{sh.email || <em>No email</em>}</div>
              ) : (
                <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                  <input
                    defaultValue={sh.email || ''}
                    onBlur={e => handleUpdateEmail(sh.id, e.target.value)}
                    placeholder="Email address"
                    type="email"
                    style={{ ...inputStyle, flex:1, fontSize:12, padding:'5px 10px' }}
                  />
                  <button
                    onClick={() => handleSendEmail(vs)}
                    disabled={!shareUrl || !isPublished || !sh.email || sending[key]}
                    style={{
                      padding:'5px 12px', flexShrink:0,
                      background: res==='sent' ? C.green : res==='error' ? C.red : (!shareUrl||!isPublished||!sh.email) ? C.border : C.navy,
                      color:C.white, border:'none', borderRadius:8,
                      cursor:(!shareUrl||!isPublished||!sh.email||sending[key])?'not-allowed':'pointer',
                      fontSize:12, fontWeight:700,
                    }}
                  >
                    {sending[key] ? '…' : res==='sent' ? '✓ Sent' : res==='error' ? '✗ Error' : sentAt ? 'Resend' : '✉ Send'}
                  </button>
                </div>
              )}

              {/* Sent status */}
              {sentAt && (
                <div style={{ fontSize:11, color:C.green, fontWeight:600, marginTop:4 }}>
                  ✓ Sent {fmtDate(sentAt)}
                </div>
              )}
              {!sentAt && !isAdvisor && isPublished && shareUrl && sh.email && (
                <div style={{ fontSize:11, color:C.amber, fontWeight:600, marginTop:4 }}>Not sent</div>
              )}

              {!isAdvisor && !sh.email && <div style={{ fontSize:10, color:C.amber, marginTop:3 }}>Add email to send report</div>}
              {!isAdvisor && !isPublished && <div style={{ fontSize:10, color:C.mid, marginTop:3 }}>Publish report before sending</div>}
              {!isAdvisor && isPublished && !shareUrl && <div style={{ fontSize:10, color:C.mid, marginTop:3 }}>Generate link before sending</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

const inputStyle = {
  padding:'7px 10px', border:`1.5px solid ${C.border}`, borderRadius:8,
  fontSize:13, fontFamily:'inherit', color:C.charcoal, background:C.white,
  outline:'none', width:'100%', boxSizing:'border-box',
};

// ── Liquidity Test Panel ──────────────────────────────────────────────────────
function LiquidityPanel({ qRecord, onSave, isAdvisor }) {
  const [cash,        setCash]        = useState(qRecord?.cash_balance         ?? '');
  const [assets,      setAssets]      = useState(qRecord?.total_assets         ?? '');
  const [liabilities, setLiabilities] = useState(qRecord?.current_liabilities  ?? '');
  const [proposed,    setProposed]    = useState(qRecord?.proposed_distribution ?? '');
  const [saving,      setSaving]      = useState(false);
  const [saved,       setSaved]       = useState(false);

  const c = parseFloat(cash)        || 0;
  const a = parseFloat(assets)      || 0;
  const l = parseFloat(liabilities) || 0;
  const p = parseFloat(proposed)    || 0;

  // Test 1: cash floor — pre-condition per Art. 9.3, cash must be >= €200k before distribution
  const floorOk = cash !== '' ? c >= 200000 : null;

  // Test 2: solvency post-distribution per Art. 9.5 — liabilities / (total_assets - proposed) <= 70%
  const assetsPost    = a > 0 && proposed !== '' ? a - p : null;
  const solvencyRatio = assetsPost !== null && assetsPost > 0 ? (l / assetsPost) * 100 : null;
  const solvencyOk    = solvencyRatio !== null ? solvencyRatio <= 70 : null;

  const allPass = floorOk && solvencyOk;

  async function handleSave() {
    setSaving(true);
    await onSave({
      cash_balance:          c || null,
      total_assets:          a || null,
      current_liabilities:   l || null,
      proposed_distribution: p || null,
    });
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  }

  const fields = [
    { label:'Cash balance (bank)',   value:cash,        setter:setCash,        hint:'From Revolut / Exact' },
    { label:'Total liabilities',     value:liabilities, setter:setLiabilities, hint:'From Exact balance sheet' },
    { label:'Total assets',          value:assets,      setter:setAssets,      hint:'From Exact balance sheet' },
    { label:'Proposed distribution', value:proposed,    setter:setProposed,    hint:'Total gross distribution amount' },
  ];

  return (
    <div style={{ background:C.white, border:`1.5px solid ${C.border}`, borderRadius:12, padding:'18px 20px' }}>
      <div style={{ fontWeight:700, fontSize:13, color:C.charcoal, marginBottom:2 }}>Liquidity / Dividend Test</div>
      <div style={{ fontSize:11, color:C.mid, marginBottom:14 }}>Per Art. 9 — cash ≥ €200k pre-distribution (9.3) · liabilities / assets ≤ 70% post-distribution (9.5)</div>

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, marginBottom:12 }}>
        {fields.map(f => (
          <div key={f.label} style={{ gridColumn:'span 1' }}>
            <div style={{ fontSize:11, fontWeight:600, color:C.mid, marginBottom:4 }}>{f.label}</div>
            {isAdvisor ? (
              <div style={{ padding:'8px 10px', border:`1px solid ${C.border}`, borderRadius:8, fontSize:13, color:C.charcoal, background:C.cream }}>
                {f.value !== '' ? `€${Number(f.value).toLocaleString('nl-NL')}` : '—'}
              </div>
            ) : (
              <input
                type="number"
                value={f.value}
                onChange={e => f.setter(e.target.value)}
                placeholder="€"
                style={inputStyle}
              />
            )}
            <div style={{ fontSize:10, color:C.border, marginTop:2 }}>{f.hint}</div>
          </div>
        ))}
      </div>

      {/* Test results */}
      {(floorOk !== null || solvencyOk !== null) && (
        <div style={{ background:allPass ? C.greenLight : C.redLight, border:`1px solid ${allPass?C.green:C.red}`, borderRadius:10, padding:'12px 14px', marginBottom:12 }}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>

            {/* Cash floor */}
            {floorOk !== null && (
              <div>
                <div style={{ fontSize:10, color:C.mid, marginBottom:2 }}>Cash floor (pre-distribution)</div>
                <div style={{ fontSize:15, fontWeight:800, color:floorOk?C.green:C.red }}>{fmt(c)}</div>
                <div style={{ fontSize:11, fontWeight:700, color:floorOk?C.green:C.red, marginTop:2 }}>
                  {floorOk ? '✓ Pass — ≥ €200k' : '✗ Fail — below €200k'}
                </div>
              </div>
            )}

            {/* Solvency */}
            {solvencyOk !== null && (
              <div>
                <div style={{ fontSize:10, color:C.mid, marginBottom:2 }}>Solvency post-distribution</div>
                <div style={{ fontSize:15, fontWeight:800, color:solvencyOk?C.green:C.red }}>
                  {solvencyRatio.toFixed(1)}% debt ratio
                </div>
                <div style={{ fontSize:11, fontWeight:700, color:solvencyOk?C.green:C.red, marginTop:2 }}>
                  {solvencyOk ? '✓ Pass — ≤ 70%' : '✗ Fail — exceeds 70%'}
                </div>
              </div>
            )}

          </div>

          {/* Summary line — only show when both tests have values */}
          {floorOk !== null && solvencyOk !== null && (
            <div style={{ marginTop:10, paddingTop:10, borderTop:`1px solid ${allPass?C.green:C.red}`, fontSize:12, fontWeight:700, color:allPass?C.green:C.red }}>
              {allPass
                ? '✓ Distribution permitted per Art. 9 — subject to GMS resolution & auditor sign-off'
                : '✗ Distribution blocked — one or more conditions not met'}
            </div>
          )}
        </div>
      )}

      {!isAdvisor && (
        <button onClick={handleSave} disabled={saving} style={{ padding:'7px 16px', background:saving?C.border:C.charcoal, color:C.white, border:'none', borderRadius:8, cursor:saving?'not-allowed':'pointer', fontSize:12, fontWeight:700 }}>
          {saving ? 'Saving…' : saved ? '✓ Saved' : 'Save'}
        </button>
      )}
    </div>
  );
}

// ── Governance Log Panel (inline create / edit / delete) ─────────────────────
const LOG_CAT_COLORS = {
  capex: '#E07B00', board_decision: '#445366', legal: '#DC3545',
  personnel: '#8B7EC8', regulatory: '#3A8FA3', distribution: '#7EA594', other: '#7A8A96',
};

function GovernanceLogPanel({ venue, quarter, year, currentUser, isAdvisor, onUpdate }) {
  const qMonths = quarter.months;
  const [entries,    setEntries]    = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [showForm,   setShowForm]   = useState(false);
  const [editingId,  setEditingId]  = useState(null);
  const [saving,     setSaving]     = useState(false);
  const emptyForm = () => ({ month: qMonths[0], category: 'board_decision', title: '', description: '', amount: '' });
  const [form, setForm] = useState(emptyForm());

  useEffect(() => { load(); }, [venue, quarter.num, year]);

  async function load() {
    setLoading(true);
    const { data } = await supabase
      .from('governance_log')
      .select('*')
      .eq('venue', venue)
      .eq('year', year)
      .in('month', qMonths)
      .order('month')
      .order('created_at');
    setEntries(data || []);
    setLoading(false);
  }

  async function handleSave() {
    if (!form.title.trim()) return;
    setSaving(true);
    const payload = {
      venue, year,
      month:       parseInt(form.month),
      category:    form.category,
      title:       form.title.trim(),
      description: form.description.trim(),
      amount:      parseFloat(form.amount) || null,
      created_by:  currentUser?.name || currentUser?.email || 'Admin',
    };
    if (editingId) {
      await supabase.from('governance_log').update(payload).eq('id', editingId);
    } else {
      await supabase.from('governance_log').insert([payload]);
    }
    setSaving(false);
    setEditingId(null);
    setForm(emptyForm());
    setShowForm(false);
    load();
    if (onUpdate) onUpdate();
  }

  async function handleDelete(id) {
    if (!window.confirm('Delete this entry?')) return;
    await supabase.from('governance_log').delete().eq('id', id);
    load();
    if (onUpdate) onUpdate();
  }

  function startEdit(entry) {
    setForm({ month: entry.month, category: entry.category, title: entry.title, description: entry.description || '', amount: entry.amount || '' });
    setEditingId(entry.id);
    setShowForm(true);
  }

  function cancelForm() {
    setEditingId(null);
    setForm(emptyForm());
    setShowForm(false);
  }

  return (
    <div style={{ background:C.white, border:`1.5px solid ${C.border}`, borderRadius:12, padding:'18px 20px' }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
        <div>
          <div style={{ fontWeight:700, fontSize:13, color:C.charcoal }}>Governance Log</div>
          <div style={{ fontSize:11, color:C.mid, marginTop:1 }}>{entries.length} {entries.length===1?'entry':'entries'} this quarter</div>
        </div>
        {!isAdvisor && !showForm && (
          <button
            onClick={() => { setShowForm(true); setEditingId(null); setForm(emptyForm()); }}
            style={{ padding:'5px 12px', background:C.navy, color:C.white, border:'none', borderRadius:8, cursor:'pointer', fontSize:12, fontWeight:700 }}
          >
            + Add
          </button>
        )}
      </div>

      {/* Inline form */}
      {showForm && !isAdvisor && (
        <div style={{ background:C.cream, borderRadius:10, padding:'14px 16px', marginBottom:14, border:`1px solid ${C.border}` }}>
          <div style={{ fontSize:12, fontWeight:700, color:C.charcoal, marginBottom:10 }}>{editingId ? 'Edit Entry' : 'New Entry'}</div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, marginBottom:8 }}>
            <div>
              <div style={{ fontSize:11, fontWeight:600, color:C.mid, marginBottom:4 }}>Month</div>
              <select value={form.month} onChange={e => setForm(f => ({...f, month: e.target.value}))} style={{ ...inputStyle, fontSize:12 }}>
                {qMonths.map(m => <option key={m} value={m}>{MONTHS[m-1]}</option>)}
              </select>
            </div>
            <div>
              <div style={{ fontSize:11, fontWeight:600, color:C.mid, marginBottom:4 }}>Category</div>
              <select value={form.category} onChange={e => setForm(f => ({...f, category: e.target.value}))} style={{ ...inputStyle, fontSize:12 }}>
                {Object.entries(LOG_CATEGORY_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
          </div>
          <div style={{ marginBottom:8 }}>
            <div style={{ fontSize:11, fontWeight:600, color:C.mid, marginBottom:4 }}>Title</div>
            <input type='text' value={form.title} onChange={e => setForm(f => ({...f, title: e.target.value}))} placeholder='Short summary…' style={{ ...inputStyle, fontSize:12 }}/>
          </div>
          <div style={{ marginBottom:8 }}>
            <div style={{ fontSize:11, fontWeight:600, color:C.mid, marginBottom:4 }}>Description</div>
            <textarea value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))} placeholder='Details…' rows={3} style={{ width:'100%', padding:'7px 10px', border:`1.5px solid ${C.border}`, borderRadius:8, fontSize:12, resize:'vertical', fontFamily:'inherit', color:C.charcoal, background:C.white, boxSizing:'border-box', outline:'none' }}/>
          </div>
          <div style={{ marginBottom:12 }}>
            <div style={{ fontSize:11, fontWeight:600, color:C.mid, marginBottom:4 }}>Amount (€) — optional</div>
            <input type='number' value={form.amount} onChange={e => setForm(f => ({...f, amount: e.target.value}))} placeholder='Leave blank if N/A' style={{ ...inputStyle, fontSize:12 }}/>
          </div>
          <div style={{ display:'flex', gap:8 }}>
            <button onClick={handleSave} disabled={saving || !form.title.trim()} style={{ padding:'7px 16px', background:saving||!form.title.trim()?C.border:C.navy, color:C.white, border:'none', borderRadius:8, cursor:saving||!form.title.trim()?'not-allowed':'pointer', fontSize:12, fontWeight:700 }}>
              {saving ? 'Saving…' : editingId ? 'Update' : 'Save Entry'}
            </button>
            <button onClick={cancelForm} style={{ padding:'7px 14px', background:C.cream, color:C.mid, border:`1.5px solid ${C.border}`, borderRadius:8, cursor:'pointer', fontSize:12, fontWeight:600 }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Entries list */}
      {loading ? (
        <div style={{ fontSize:12, color:C.mid }}>Loading…</div>
      ) : entries.length === 0 ? (
        <div style={{ background:C.cream, borderRadius:8, padding:12, textAlign:'center', fontSize:12, color:C.mid }}>No entries this quarter.</div>
      ) : entries.map(entry => {
        const cc = LOG_CAT_COLORS[entry.category] || C.mid;
        return (
          <div key={entry.id} style={{ background:C.cream, borderRadius:8, padding:'10px 14px', marginBottom:8, borderLeft:`3px solid ${cc}` }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:4 }}>
              <span style={{ fontSize:11, fontWeight:700, color:cc }}>{LOG_CATEGORY_LABELS[entry.category] || entry.category}</span>
              <div style={{ display:'flex', gap:6, alignItems:'center' }}>
                <span style={{ fontSize:10, color:C.mid }}>{MONTHS[entry.month-1]}</span>
                {entry.amount && <span style={{ fontSize:12, fontWeight:700, color:C.charcoal }}>{fmt(entry.amount)}</span>}
                {!isAdvisor && (
                  <>
                    <button onClick={() => startEdit(entry)} style={{ padding:'2px 8px', background:C.white, color:C.navy, border:`1px solid ${C.border}`, borderRadius:6, cursor:'pointer', fontSize:11, fontWeight:600 }}>Edit</button>
                    <button onClick={() => handleDelete(entry.id)} style={{ padding:'2px 8px', background:C.white, color:C.red, border:`1px solid ${C.border}`, borderRadius:6, cursor:'pointer', fontSize:11, fontWeight:600 }}>Del</button>
                  </>
                )}
              </div>
            </div>
            <div style={{ fontSize:13, fontWeight:600, color:C.charcoal, marginBottom:entry.description?3:0 }}>{entry.title}</div>
            {entry.description && <div style={{ fontSize:11, color:C.mid, lineHeight:1.5 }}>{entry.description}</div>}
          </div>
        );
      })}
    </div>
  );
}

// ── Board Minutes Panel ───────────────────────────────────────────────────────
const DIRECTORS = ['Elliott', 'Yudhi', 'Alex'];

function BoardMinutesPanel({ venue, quarter, year, isAdvisor }) {
  const [expanded,    setExpanded]    = useState(false);
  const [minutes,     setMinutes]     = useState({}); // keyed by month number
  const [saving,      setSaving]      = useState({});
  const [saved,       setSaved]       = useState({});
  const [loading,     setLoading]     = useState(true);

  const qMonths = quarter.months;

  useEffect(() => { loadMinutes(); }, [venue, quarter.num, year]);

  async function loadMinutes() {
    setLoading(true);
    const { data } = await supabase
      .from('board_minutes')
      .select('*')
      .eq('venue', venue)
      .eq('year', year)
      .in('month', qMonths);
    if (data) {
      const map = {};
      data.forEach(r => { map[r.month] = r; });
      // Ensure all quarter months have an entry in state
      qMonths.forEach(m => {
        if (!map[m]) map[m] = { month: m, meeting_date: '', notes: '', attendees: { Elliott: true, Yudhi: true, Alex: true } };
      });
      setMinutes(map);
    } else {
      const map = {};
      qMonths.forEach(m => { map[m] = { month: m, meeting_date: '', notes: '', attendees: { Elliott: true, Yudhi: true, Alex: true } }; });
      setMinutes(map);
    }
    setLoading(false);
  }

  function updateField(month, field, value) {
    setMinutes(prev => ({ ...prev, [month]: { ...prev[month], [field]: value } }));
  }

  function toggleAttendee(month, name) {
    setMinutes(prev => ({
      ...prev,
      [month]: {
        ...prev[month],
        attendees: { ...prev[month].attendees, [name]: !prev[month].attendees?.[name] },
      },
    }));
  }

  async function handleSave(month) {
    setSaving(p => ({ ...p, [month]: true }));
    const entry = minutes[month];
    const payload = {
      venue, year, month,
      meeting_date:  entry.meeting_date || null,
      notes:         entry.notes        || null,
      attendees:     entry.attendees    || {},
      updated_at:    new Date().toISOString(),
    };
    if (entry.id) {
      await supabase.from('board_minutes').update(payload).eq('id', entry.id);
    } else {
      const { data } = await supabase.from('board_minutes').insert([payload]).select().single();
      if (data) setMinutes(p => ({ ...p, [month]: { ...p[month], id: data.id } }));
    }
    setSaving(p => ({ ...p, [month]: false }));
    setSaved(p => ({ ...p, [month]: true }));
    setTimeout(() => setSaved(p => ({ ...p, [month]: false })), 2500);
  }

  const entryCount = qMonths.filter(m => minutes[m]?.notes?.trim()).length;

  return (
    <div style={{ background:C.white, border:`1.5px solid ${C.border}`, borderRadius:12, overflow:'hidden' }}>
      {/* Header — collapsible */}
      <div
        onClick={() => setExpanded(p => !p)}
        style={{ padding:'14px 18px', cursor:'pointer', display:'flex', justifyContent:'space-between', alignItems:'center', background:expanded?C.cream:C.white, borderBottom:expanded?`1px solid ${C.border}`:'none' }}
      >
        <div>
          <div style={{ fontWeight:700, fontSize:13, color:C.charcoal }}>Board Minutes</div>
          <div style={{ fontSize:11, color:C.mid, marginTop:1 }}>Internal only · {entryCount} of {qMonths.length} months recorded</div>
        </div>
        <span style={{ fontSize:18, color:C.mid }}>{expanded ? '▲' : '▼'}</span>
      </div>

      {expanded && (
        <div style={{ padding:'16px 18px', display:'flex', flexDirection:'column', gap:14 }}>
          {loading ? (
            <div style={{ fontSize:12, color:C.mid }}>Loading…</div>
          ) : qMonths.map(m => {
            const entry    = minutes[m] || {};
            const hasNotes = entry.notes?.trim();
            return (
              <div key={m} style={{ background:hasNotes?C.cream:'#FAFAFA', borderRadius:10, padding:'14px 16px', border:`1px solid ${hasNotes?C.border:'#EAECEF'}` }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
                  <div style={{ fontWeight:700, fontSize:13, color:C.charcoal }}>{MONTHS[m-1]}</div>
                  {hasNotes && <span style={{ fontSize:10, background:C.greenLight, color:C.green, padding:'2px 8px', borderRadius:10, fontWeight:700, border:`1px solid ${C.green}` }}>● Recorded</span>}
                </div>

                {/* Meeting date */}
                <div style={{ marginBottom:10 }}>
                  <div style={{ fontSize:11, fontWeight:600, color:C.mid, marginBottom:4 }}>Meeting date</div>
                  {isAdvisor ? (
                    <div style={{ fontSize:12, color:C.charcoal }}>{entry.meeting_date || '—'}</div>
                  ) : (
                    <input
                      type="date"
                      value={entry.meeting_date || ''}
                      onChange={e => updateField(m, 'meeting_date', e.target.value)}
                      style={{ ...inputStyle, fontSize:12 }}
                    />
                  )}
                </div>

                {/* Attendees */}
                <div style={{ marginBottom:10 }}>
                  <div style={{ fontSize:11, fontWeight:600, color:C.mid, marginBottom:6 }}>Attendees</div>
                  <div style={{ display:'flex', gap:8 }}>
                    {DIRECTORS.map(name => {
                      const present = entry.attendees?.[name] !== false;
                      return isAdvisor ? (
                        <span key={name} style={{ fontSize:11, padding:'3px 10px', borderRadius:20, fontWeight:700, background:present?C.greenLight:C.redLight, color:present?C.green:C.red, border:`1px solid ${present?C.green:C.red}` }}>
                          {present ? '✓' : '✗'} {name}
                        </span>
                      ) : (
                        <button
                          key={name}
                          onClick={() => toggleAttendee(m, name)}
                          style={{ fontSize:11, padding:'4px 12px', borderRadius:20, fontWeight:700, cursor:'pointer', border:`1.5px solid ${present?C.green:C.border}`, background:present?C.greenLight:C.white, color:present?C.green:C.mid, transition:'all 0.1s' }}
                        >
                          {present ? '✓' : '○'} {name}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Notes */}
                <div style={{ marginBottom: isAdvisor ? 0 : 10 }}>
                  <div style={{ fontSize:11, fontWeight:600, color:C.mid, marginBottom:4 }}>Minutes / Notes</div>
                  {isAdvisor ? (
                    <div style={{ fontSize:12, color:C.charcoal, lineHeight:1.6, whiteSpace:'pre-wrap' }}>{entry.notes || <span style={{ color:C.border }}>No minutes recorded.</span>}</div>
                  ) : (
                    <textarea
                      value={entry.notes || ''}
                      onChange={e => updateField(m, 'notes', e.target.value)}
                      placeholder={`${MONTHS[m-1]} board meeting notes — decisions, actions, discussion points…`}
                      rows={4}
                      style={{ width:'100%', padding:'8px 10px', border:`1.5px solid ${C.border}`, borderRadius:8, fontSize:12, resize:'vertical', fontFamily:'inherit', lineHeight:1.6, color:C.charcoal, background:C.white, boxSizing:'border-box', outline:'none' }}
                    />
                  )}
                </div>

                {!isAdvisor && (
                  <button
                    onClick={() => handleSave(m)}
                    disabled={saving[m]}
                    style={{ padding:'6px 14px', background:saving[m]?C.border:C.charcoal, color:C.white, border:'none', borderRadius:8, cursor:saving[m]?'not-allowed':'pointer', fontSize:12, fontWeight:700 }}
                  >
                    {saving[m] ? 'Saving…' : saved[m] ? '✓ Saved' : 'Save'}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Quarter panel ─────────────────────────────────────────────────────────────
function QuarterPanel({ venue, quarter, year, monthly, monthlyLY, dailyRev, dailyRevLY, quarterRecord, onPublishToggle, publishing, currentUser, onQuarterUpdate, onIncentiveAction, isAdvisor }) {
  const [expanded,        setExpanded]        = useState(false);
  const [narrative,       setNarrative]       = useState(quarterRecord?.narrative || '');
  const [savingNarrative, setSavingNarrative] = useState(false);
  const [narrativeSaved,  setNarrativeSaved]  = useState(false);
  const [isMobile,        setIsMobile]        = useState(window.innerWidth < 768);

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  const isSassys  = venue === 'Sassys';
  const qMonths   = quarter.months;
  const nowMonth  = new Date().getMonth() + 1;
  const nowYear   = new Date().getFullYear();
  const sum       = (arr, key) => arr.reduce((acc, r) => acc + (r?.[key] || 0), 0);

  // For each quarter month: if it's the current open month, pro-rate budget costs
  // Otherwise use actual costs from results_monthly
  const monthCost = (monthNum, key, budgetKey) => {
    const row = monthly.find(r => r.month === monthNum);
    if (!row) return 0;
    const isOpenMonth = year === nowYear && monthNum === nowMonth;
    if (!isOpenMonth) return row[key] || 0;
    // Pro-rate: use days elapsed in month based on latest daily revenue
    const daysInMth  = new Date(nowYear, nowMonth, 0).getDate();
    const latestDay  = latestDayOfMonthGov(dailyRev, monthNum);
    const frac       = latestDay ? Math.min(latestDay / daysInMth, 1) : 0;
    const actual     = row[key] || 0;
    const budget     = row[budgetKey] || 0;
    // If actual cost has been entered (month end upload), use it; otherwise pro-rate budget
    return actual > 0 ? actual : budget * frac;
  };

  const cyRows = qMonths.map(m => monthly.find(r => r.month === m)).filter(Boolean);
  const lyRows = qMonths.map(m => monthlyLY.find(r => r.month === m)).filter(Boolean);

  const cy = {
    revenue:         qMonths.reduce((s,m) => s + (govDailyRev(dailyRev, m) || sum([monthly.find(r=>r.month===m)].filter(Boolean),'actual_revenue')), 0),
    food_cost:       qMonths.reduce((s,m) => s + monthCost(m, 'food_cost',       'budget_food_cost'), 0),
    bev_cost:        qMonths.reduce((s,m) => s + monthCost(m, 'bev_cost',        'budget_bev_cost'), 0),
    oedipus:         qMonths.reduce((s,m) => s + monthCost(m, 'oedipus_rent',    'budget_oedipus_rent')
                                              + monthCost(m, 'oedipus_utilities','budget_oedipus_utilities')
                                              + monthCost(m, 'oedipus_rev_share','budget_oedipus_rev_share'), 0),
    kitchen_payroll: qMonths.reduce((s,m) => s + monthCost(m, 'kitchen_payroll', 'budget_kitchen_payroll'), 0),
    foh_payroll:     qMonths.reduce((s,m) => s + monthCost(m, 'foh_payroll',     'budget_foh_payroll'), 0),
    lm_total:        qMonths.reduce((s,m) => s + sum([monthly.find(r=>r.month===m)].filter(Boolean),'lm_prep_payroll_alloc')
                                              + sum([monthly.find(r=>r.month===m)].filter(Boolean),'lm_transfers_alloc'), 0),
    group_costs:     qMonths.reduce((s,m) => s + monthCost(m, 'group_costs',     'budget_group_costs'), 0),
    other_opex:      qMonths.reduce((s,m) => s + monthCost(m, 'other_opex',      'budget_other_opex'), 0),
    net_profit:      sum(cyRows, 'net_profit'),
    covers:          sum(cyRows, 'actual_covers'),
    food_revenue:    sum(cyRows, 'food_revenue'),
    bev_revenue:     sum(cyRows, 'bev_revenue'),
  };
  cy.payroll = cy.kitchen_payroll + (isSassys ? 0 : cy.foh_payroll);
  cy.ebitda  = cy.revenue - cy.food_cost
    - (isSassys ? 0 : cy.bev_cost) - (isSassys ? cy.oedipus : 0)
    - cy.payroll - cy.lm_total - cy.group_costs - cy.other_opex;
  cy.totalExpense = cy.revenue - cy.ebitda;

  const ly = {
    revenue:         qMonths.reduce((s,m) => s + (govDailyRev(dailyRevLY, m) || sum([monthlyLY.find(r=>r.month===m)].filter(Boolean),'actual_revenue')), 0),
    food_cost:       sum(lyRows, 'food_cost'),
    bev_cost:        sum(lyRows, 'bev_cost'),
    oedipus:         sum(lyRows, 'oedipus_rent') + sum(lyRows, 'oedipus_utilities') + sum(lyRows, 'oedipus_rev_share'),
    kitchen_payroll: sum(lyRows, 'kitchen_payroll'),
    foh_payroll:     sum(lyRows, 'foh_payroll'),
    lm_total:        sum(lyRows, 'lm_prep_payroll_alloc') + sum(lyRows, 'lm_transfers_alloc'),
    group_costs:     sum(lyRows, 'group_costs'),
    other_opex:      sum(lyRows, 'other_opex'),
    net_profit:      sum(lyRows, 'net_profit'),
    covers:          sum(lyRows, 'actual_covers'),
    food_revenue:    sum(lyRows, 'food_revenue'),
    bev_revenue:     sum(lyRows, 'bev_revenue'),
  };
  ly.payroll = ly.kitchen_payroll + (isSassys ? 0 : ly.foh_payroll);
  ly.ebitda  = ly.revenue - ly.food_cost
    - (isSassys ? 0 : ly.bev_cost) - (isSassys ? ly.oedipus : 0)
    - ly.payroll - ly.lm_total - ly.group_costs - ly.other_opex;
  ly.totalExpense = ly.revenue - ly.ebitda;

  const INCENTIVE_THRESHOLD = 200000;
  const DIRECTOR_COUNT      = 3;
  const incentiveEarned = cy.revenue > INCENTIVE_THRESHOLD; // derived from revenue, not DB flag
  const incentivePaid   = quarterRecord?.incentive_fee_paid   || false;
  const incentiveWaived = quarterRecord?.incentive_fee_waived || false;
  const incentiveAmount = incentiveEarned ? Math.round((cy.revenue - INCENTIVE_THRESHOLD) * 0.05) : 0;
  const incentivePerDir = Math.round(incentiveAmount / DIRECTOR_COUNT);

  const handleSaveNarrative = async () => {
    setSavingNarrative(true);
    const payload = { venue, year, quarter:quarter.num, narrative, updated_at: new Date().toISOString() };
    if (quarterRecord) {
      await supabase.from('governance_quarterly').update(payload).eq('id', quarterRecord.id);
    } else {
      await supabase.from('governance_quarterly').insert([{
        ...payload, published:false,
        incentive_fee_earned:false, incentive_fee_paid:false, incentive_fee_waived:false,
      }]);
    }
    setSavingNarrative(false);
    setNarrativeSaved(true);
    setTimeout(() => setNarrativeSaved(false), 2500);
  };

  const handleLiquiditySave = async (vals) => {
    const payload = { venue, year, quarter:quarter.num, ...vals, updated_at: new Date().toISOString() };
    if (quarterRecord) {
      await supabase.from('governance_quarterly').update(payload).eq('id', quarterRecord.id);
    } else {
      await supabase.from('governance_quarterly').insert([{
        ...payload, published:false,
        incentive_fee_earned:false, incentive_fee_paid:false, incentive_fee_waived:false,
      }]);
    }
    if (onQuarterUpdate) onQuarterUpdate();
  };

  if (cyRows.length === 0) return (
    <div style={{ background:C.white, borderRadius:12, border:`1px solid ${C.border}`, padding:'20px 24px', opacity:0.5 }}>
      <div style={{ fontWeight:700, fontSize:14, color:C.charcoal, marginBottom:4 }}>{quarter.label} {year}</div>
      <div style={{ fontSize:12, color:C.mid }}>No data for this quarter.</div>
    </div>
  );

  const badgeBg    = incentivePaid ? C.greenLight : C.amberLight;
  const badgeColor = incentivePaid ? C.green      : C.amber;
  const badgeText  = incentivePaid ? `✓ Incentive Fee Paid for Q${quarter.num}` : incentiveWaived ? `Incentive Fee Waived for Q${quarter.num}` : '⚠ Incentive Due';

  return (
    <div style={{ background:C.white, borderRadius:12, border:`1px solid ${C.border}`, overflow:'hidden', boxShadow:'0 1px 6px rgba(0,0,0,0.06)' }}>

      {/* Collapsed header */}
      <div onClick={() => setExpanded(!expanded)} style={{ padding:'16px 20px', cursor:'pointer', display:'flex', justifyContent:'space-between', alignItems:'center', background:expanded?C.cream:C.white, borderBottom:expanded?`1px solid ${C.border}`:'none' }}>
        <div style={{ display:'flex', alignItems:'center', gap:isMobile?10:20, flexWrap:'wrap' }}>
          <div>
            <div style={{ fontWeight:700, fontSize:15, color:C.charcoal }}>{quarter.label} {year}</div>
            <div style={{ fontSize:11, color:C.mid }}>{quarter.months.map(m => MONTHS[m-1]).join(' · ')}</div>
          </div>
          <div style={{ display:'flex', gap:isMobile?12:24, flexWrap:'wrap' }}>
            {[
              { label:'Revenue', value:fmt(cy.revenue),            color:C.charcoal },
              { label:'EBITDA',  value:fmt(Math.round(cy.ebitda)), color:cy.ebitda>=0?C.green:C.red },
              ...(!isMobile && cy.net_profit !== 0 ? [{ label:'Net Profit', value:fmt(cy.net_profit), color:cy.net_profit>=0?C.green:C.red }] : []),
            ].map(item => (
              <div key={item.label} style={{ textAlign:'center' }}>
                <div style={{ fontSize:'10px', color:C.mid, fontWeight:600, textTransform:'uppercase' }}>{item.label}</div>
                <div style={{ fontSize:'14px', fontWeight:800, color:item.color }}>{item.value}</div>
              </div>
            ))}
          </div>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:10, flexShrink:0, marginLeft:8 }}>
          {venue === 'Box Jordaan' && incentiveEarned && !isMobile && (
            <span style={{ padding:'3px 10px', borderRadius:20, fontSize:11, fontWeight:700, background:badgeBg, color:badgeColor, border:`1px solid ${badgeColor}` }}>{badgeText}</span>
          )}
          {quarterRecord?.published && (
            <span style={{ fontSize:10, background:C.green, color:C.white, padding:'2px 8px', borderRadius:20, fontWeight:700 }}>● Live</span>
          )}
          <span style={{ fontSize:20, color:C.mid }}>{expanded ? '▲' : '▼'}</span>
        </div>
      </div>

      {expanded && (
        <div style={{ padding: isMobile ? '16px' : '24px' }}>
          <div style={{ display:'grid', gridTemplateColumns: isMobile ? '1fr' : '1.4fr 1fr', gap:24, alignItems:'start' }}>

            {/* LEFT */}
            <div style={{ display:'flex', flexDirection:'column', gap:20, minWidth:0 }}>

              {/* P&L table */}
              <div style={{ overflowX:'auto' }}>
                <table style={{ width:'100%', borderCollapse:'collapse' }}>
                  <thead>
                    <tr style={{ background:C.cream, borderBottom:`1px solid ${C.border}` }}>
                      {['', 'Actual', String(year-1), 'vs LY %'].map((h, i) => (
                        <th key={i} style={{ padding:isMobile?'7px 6px':'9px 16px', textAlign:i===0?'left':'right', fontSize:'10px', fontWeight:700, color:C.mid, textTransform:'uppercase', letterSpacing:'0.04em' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <PLRow label='Revenue'         actual={cy.revenue}     ly={ly.revenue}     bold isRevenue isMobile={isMobile}/>
                    <PLRow label='Food Cost'        actual={cy.food_cost}   ly={ly.food_cost}   indent isMobile={isMobile}/>
                    {!isSassys && <PLRow label='Bev Cost'      actual={cy.bev_cost}    ly={ly.bev_cost}    indent dimIfZero isMobile={isMobile}/>}
                    {isSassys  && <PLRow label='Oedipus Costs' actual={cy.oedipus}     ly={ly.oedipus}     indent dimIfZero isMobile={isMobile}/>}
                    <PLRow label='Payroll'          actual={cy.payroll}     ly={ly.payroll}     indent isMobile={isMobile}/>
                    {cy.lm_total > 0 && <PLRow label='LM Allocations' actual={cy.lm_total} ly={ly.lm_total>0?ly.lm_total:null} indent dimIfZero isMobile={isMobile}/>}
                    <PLRow label='Management Fees'  actual={cy.group_costs} ly={ly.group_costs} indent dimIfZero isMobile={isMobile}/>
                    <PLRow label='Other OpEx'       actual={cy.other_opex}     ly={ly.other_opex}     indent dimIfZero isMobile={isMobile}/>
                    <PLRow label='Total Expenses'   actual={cy.totalExpense}   ly={ly.totalExpense}   bold isMobile={isMobile}/>
                    <PLRow label='EBITDA'           actual={cy.ebitda}         ly={ly.ebitda}         bold highlight isRevenue isMobile={isMobile}/>
                    {cy.net_profit !== 0 && <PLRow label='Net Profit' actual={cy.net_profit} ly={ly.net_profit} bold isRevenue isMobile={isMobile}/>}
                  </tbody>
                </table>
              </div>

              {/* KPI chips */}
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(120px, 1fr))', gap:10 }}>
                {[
                  { label:'Food Cost %', actual:fmtPct(cy.food_cost, cy.food_revenue), ly:fmtPct(ly.food_cost, ly.food_revenue) },
                  ...(!isSassys ? [{ label:'Bev Cost %', actual:fmtPct(cy.bev_cost, cy.bev_revenue), ly:fmtPct(ly.bev_cost, ly.bev_revenue) }] : []),
                  ...(isSassys  ? [{ label:'Oedipus %',  actual:fmtPct(cy.oedipus,  cy.revenue), ly:fmtPct(ly.oedipus,  ly.revenue) }] : []),
                  { label:'Payroll %',  actual:fmtPct(cy.payroll, cy.revenue),   ly:fmtPct(ly.payroll, ly.revenue) },
                  { label:'Covers',     actual:fmtNum(cy.covers),                ly:fmtNum(ly.covers) },
                  { label:'Avg Check',  actual:cy.covers ? fmt(Math.round(cy.revenue/cy.covers)) : '—', ly:ly.covers ? fmt(Math.round(ly.revenue/ly.covers)) : '—' },
                ].map(item => (
                  <div key={item.label} style={{ background:C.cream, borderRadius:8, padding:'10px 14px', border:`1px solid ${C.border}` }}>
                    <div style={{ fontSize:'10px', color:C.mid, textTransform:'uppercase', fontWeight:700, marginBottom:3 }}>{item.label}</div>
                    <div style={{ fontSize:'15px', fontWeight:800, color:C.charcoal }}>{item.actual}</div>
                    {item.ly && item.ly !== '—' && <div style={{ fontSize:'10px', color:C.mid, marginTop:2 }}>LY: {item.ly}</div>}
                  </div>
                ))}
              </div>

              {/* Monthly breakdown */}
              <div>
                <div style={{ fontSize:'11px', fontWeight:700, color:C.mid, textTransform:'uppercase', letterSpacing:'0.04em', marginBottom:10 }}>Monthly Breakdown</div>
                <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'12px' }}>
                  <thead>
                    <tr style={{ background:C.cream, borderBottom:`1px solid ${C.border}` }}>
                      {['Month','Revenue','EBITDA','Covers'].map((h,i) => (
                        <th key={h} style={{ padding:'7px 10px', textAlign:i===0?'left':'right', fontSize:'10px', fontWeight:700, color:C.mid, textTransform:'uppercase' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {qMonths.map(m => {
                      const r       = monthly.find(x => x.month === m);
                      const rev     = govDailyRev(dailyRev, m) || r?.actual_revenue || null;
                      const eb      = r ? calcEBITDA(r, isSassys) : null;
                      const covers  = r?.actual_covers || null;
                      return (
                        <tr key={m} style={{ borderBottom:`1px solid ${C.border}` }}>
                          <td style={{ padding:'8px 10px', fontWeight:700 }}>{MONTHS[m-1]}</td>
                          <td style={{ padding:'8px 10px', textAlign:'right', fontWeight:600 }}>{rev ? fmt(rev) : '—'}</td>
                          <td style={{ padding:'8px 10px', textAlign:'right', fontWeight:700, color:eb!=null?(eb>=0?C.green:C.red):C.border }}>{eb != null ? fmt(Math.round(eb)) : '—'}</td>
                          <td style={{ padding:'8px 10px', textAlign:'right' }}>{covers || '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Art. 8.6 — Box Jordaan only */}
              {venue === 'Box Jordaan' && (
                <div style={{ background:incentiveEarned?(incentivePaid?C.greenLight:C.amberLight):C.cream, border:`1px solid ${incentiveEarned?(incentivePaid?C.green:C.amber):C.border}`, borderRadius:10, padding:'14px 18px' }}>
                  <div style={{ fontSize:'11px', fontWeight:700, color:C.mid, textTransform:'uppercase', letterSpacing:'0.04em', marginBottom:10 }}>Article 8.6 — Quarterly Incentive Fee</div>
                  <div style={{ display:'flex', gap:24, flexWrap:'wrap', marginBottom:incentiveEarned?12:0 }}>
                    <div><div style={{ fontSize:11, color:C.mid, marginBottom:2 }}>Q Revenue</div><div style={{ fontSize:14, fontWeight:700 }}>{fmt(cy.revenue)}</div></div>
                    <div><div style={{ fontSize:11, color:C.mid, marginBottom:2 }}>Threshold</div><div style={{ fontSize:14, fontWeight:700 }}>€200,000</div></div>
                    <div><div style={{ fontSize:11, color:C.mid, marginBottom:2 }}>Fee (5% above)</div><div style={{ fontSize:14, fontWeight:700, color:cy.revenue>INCENTIVE_THRESHOLD?C.amber:C.mid }}>{cy.revenue>INCENTIVE_THRESHOLD?fmt(incentiveAmount):'Not applicable'}</div></div>
                    <div>
                      <div style={{ fontSize:11, color:C.mid, marginBottom:2 }}>Status</div>
                      <div style={{ fontSize:13, fontWeight:700, color:incentivePaid?C.green:incentiveEarned?C.amber:C.mid }}>
                        {!incentiveEarned?'Not earned this quarter':incentivePaid?`✓ Incentive Fee Paid for Q${quarter.num} — ${fmt(incentivePerDir)} per director`:incentiveWaived?`Incentive Fee Waived for Q${quarter.num}`:'⚠ Earned — not yet paid'}
                      </div>
                    </div>
                  </div>
                  {incentiveEarned && !incentivePaid && !incentiveWaived && !isAdvisor && (
                    <div style={{ display:'flex', gap:10, marginTop:4 }}>
                      <button
                        onClick={() => onIncentiveAction(quarterRecord, venue, quarter.num, year, 'paid')}
                        style={{ flex:1, padding:'9px 14px', background:C.green, color:C.white, border:'none', borderRadius:8, cursor:'pointer', fontSize:13, fontWeight:700 }}
                      >
                        ✓ Pay Fee
                      </button>
                      <button
                        onClick={() => onIncentiveAction(quarterRecord, venue, quarter.num, year, 'waived')}
                        style={{ flex:1, padding:'9px 14px', background:C.cream, color:C.mid, border:`1.5px solid ${C.border}`, borderRadius:8, cursor:'pointer', fontSize:13, fontWeight:700 }}
                      >
                        Don't Pay
                      </button>
                    </div>
                  )}
                  {incentiveEarned && (incentivePaid || incentiveWaived) && !isAdvisor && (
                    <button
                      onClick={() => onIncentiveAction(quarterRecord, venue, quarter.num, year, 'reset')}
                      style={{ fontSize:11, color:C.mid, background:'none', border:'none', cursor:'pointer', padding:0, textDecoration:'underline', marginTop:4 }}
                    >
                      Reset
                    </button>
                  )}
                  {incentiveEarned && !incentiveWaived && (
                    <div style={{ background:'rgba(255,255,255,0.6)', borderRadius:8, padding:'10px 14px', display:'flex', gap:24, flexWrap:'wrap', marginTop:incentivePaid||incentiveWaived?8:0 }}>
                      {['Director 1','Director 2','Director 3 (Elliott)'].map((d,i) => (
                        <div key={i}><div style={{ fontSize:10, color:C.mid, marginBottom:2 }}>{d}</div><div style={{ fontSize:14, fontWeight:800 }}>{fmt(incentivePerDir)}</div></div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* RIGHT */}
            <div style={{ display:'flex', flexDirection:'column', gap:16, minWidth:0 }}>

              {/* Board minutes — internal only, never published */}
              <BoardMinutesPanel venue={venue} quarter={quarter} year={year} isAdvisor={isAdvisor} />

              {/* Board narrative */}
              <div style={{ background:C.white, border:`1.5px solid ${C.border}`, borderRadius:12, padding:'18px 20px' }}>
                <div style={{ fontWeight:700, fontSize:13, color:C.charcoal, marginBottom:4 }}>Board Narrative</div>
                <div style={{ fontSize:11, color:C.mid, marginBottom:12 }}>Visible to shareholders via their link when published.</div>
                {isAdvisor ? (
                  <div style={{ padding:'9px 12px', border:`1.5px solid ${C.border}`, borderRadius:8, fontSize:13, color:C.charcoal, background:C.cream, lineHeight:1.6, minHeight:80, whiteSpace:'pre-wrap' }}>
                    {narrative || <span style={{ color:C.mid }}>No narrative entered.</span>}
                  </div>
                ) : (
                  <>
                    <textarea
                      value={narrative}
                      onChange={e => setNarrative(e.target.value)}
                      placeholder={`Q${quarter.num} ${year} summary — highlights, decisions, context for shareholders…`}
                      rows={5}
                      style={{ width:'100%', padding:'9px 12px', border:`1.5px solid ${C.border}`, borderRadius:8, fontSize:13, resize:'vertical', fontFamily:'inherit', lineHeight:1.6, color:C.charcoal, background:'#FAFAFA', boxSizing:'border-box', marginBottom:10, outline:'none' }}
                    />
                    <button onClick={handleSaveNarrative} disabled={savingNarrative} style={{ padding:'7px 16px', background:savingNarrative?C.border:C.charcoal, color:C.white, border:'none', borderRadius:8, cursor:savingNarrative?'not-allowed':'pointer', fontSize:12, fontWeight:600 }}>
                      {savingNarrative ? 'Saving…' : narrativeSaved ? '✓ Saved' : 'Save Narrative'}
                    </button>
                  </>
                )}
              </div>

              {/* Governance log — inline create/edit/delete */}
              <GovernanceLogPanel
                venue={venue}
                quarter={quarter}
                year={year}
                currentUser={currentUser}
                isAdvisor={isAdvisor}
                onUpdate={onQuarterUpdate}
              />

              {/* Liquidity test */}
              <LiquidityPanel qRecord={quarterRecord} onSave={handleLiquiditySave} isAdvisor={isAdvisor} />

              {/* Shareholder registry */}
              <ShareholderRegistryPanel
                venue={venue}
                quarter={quarter.num}
                year={year}
                qRecord={quarterRecord}
                onPublishToggle={() => onPublishToggle(quarterRecord, venue, quarter.num, year, narrative)}
                publishing={publishing}
                currentUser={currentUser}
                isAdvisor={isAdvisor}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── MAIN ──────────────────────────────────────────────────────────────────────
export default function Governance({ currentUser }) {
  const isAdmin   = isAdminUser(currentUser);
  const isAdvisor = currentUser?.role === 'advisor';
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  if (!isAdmin && !isAdvisor) return (
    <div style={{ padding:'3rem', textAlign:'center', color:C.mid }}>
      <div style={{ fontSize:32, marginBottom:12 }}>🔒</div>
      <div style={{ fontSize:15, fontWeight:700, color:C.charcoal, marginBottom:8 }}>Admin access required</div>
      <div style={{ fontSize:13, color:C.mid }}>Shareholders access quarterly reports via a personalised link sent by the board.</div>
    </div>
  );

  const [venue,      setVenue]      = useState('Box Jordaan');
  const [year,       setYear]       = useState(CURRENT_YEAR);
  const [loading,    setLoading]    = useState(true);
  const [publishing, setPublishing] = useState(false);
  const [monthly,    setMonthly]    = useState([]);
  const [monthlyLY,  setMonthlyLY]  = useState([]);
  const [quarterly,  setQuarterly]  = useState([]);
  const [dailyRev,   setDailyRev]   = useState([]);
  const [dailyRevLY, setDailyRevLY] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    const [{ data:mData },{ data:mLY },{ data:qData },{ data:dData },{ data:dDataLY }] = await Promise.all([
      supabase.from('results_monthly').select('*').eq('venue', venue).eq('year', year).order('month'),
      supabase.from('results_monthly').select('*').eq('venue', venue).eq('year', year-1).order('month'),
      supabase.from('governance_quarterly').select('*').eq('venue', venue).eq('year', year),
      supabase.from('results_daily').select('date,total_revenue').eq('venue', venue).eq('year', year),
      supabase.from('results_daily').select('date,total_revenue').eq('venue', venue).eq('year', year-1),
    ]);
    if (mData) setMonthly(mData);
    if (mLY)   setMonthlyLY(mLY);
    if (qData) setQuarterly(qData);
    if (dData)   setDailyRev(dData);
    if (dDataLY) setDailyRevLY(dDataLY);
    setLoading(false);
  }, [venue, year]);

  useEffect(() => { load(); }, [load]);

  const handlePublishToggle = async (qRecord, v, q, y, narrative) => {
    setPublishing(true);
    const isPublished = qRecord?.published || false;
    const updates = {
      venue:v, year:y, quarter:q,
      published: !isPublished,
      published_at: !isPublished ? new Date().toISOString() : null,
      published_by: !isPublished ? (currentUser?.name || currentUser?.email) : null,
      narrative: narrative || '',
      updated_at: new Date().toISOString(),
    };
    if (qRecord) {
      await supabase.from('governance_quarterly').update(updates).eq('id', qRecord.id);
    } else {
      await supabase.from('governance_quarterly').insert([{
        ...updates, incentive_fee_earned:false, incentive_fee_paid:false, incentive_fee_waived:false,
      }]);
    }
    setPublishing(false);
    load();
  };

  const handleIncentiveAction = async (qRecord, v, q, y, action) => {
    const paid   = action === 'paid';
    const waived = action === 'waived';
    const updates = {
      venue:v, year:y, quarter:q,
      incentive_fee_paid:   paid,
      incentive_fee_waived: waived,
      updated_at: new Date().toISOString(),
    };
    if (qRecord) {
      await supabase.from('governance_quarterly').update(updates).eq('id', qRecord.id);
    } else {
      await supabase.from('governance_quarterly').insert([{
        ...updates, published:false, narrative:'', incentive_fee_earned: false,
      }]);
    }

    // Auto-log governance entry for paid or waived — only on initial action, not reset
    if (paid || waived) {
      const qMonths    = QUARTERS.find(qt => qt.num === q)?.months || [];
      const lastMonth  = qMonths[qMonths.length - 1];
      // Use same revenue source as the panel — dailyRev primary, monthly fallback
      const qRevenue   = qMonths.reduce((s, m) =>
        s + (govDailyRev(dailyRev, m) || monthly.find(r => r.month === m)?.actual_revenue || 0), 0);
      const feeTotal   = qRevenue > 200000 ? Math.round((qRevenue - 200000) * 0.05) : 0;
      const feePerDir  = Math.round(feeTotal / 3);
      const fmtE       = (n) => `€${Number(n).toLocaleString('en-US', { maximumFractionDigits:0 })}`;

      const logEntry = paid ? {
        title:       `Q${q} ${y} Incentive Fee Paid`,
        description: `Art. 8.6 incentive fee of ${fmtE(feeTotal)} paid for Q${q} ${y}. ${fmtE(feePerDir)} per director. Revenue: ${fmtE(qRevenue)}.`,
      } : {
        title:       `Q${q} ${y} Incentive Fee Not Paid`,
        description: `Art. 8.6 incentive fee of ${fmtE(feeTotal)} not paid for Q${q} ${y}. Revenue: ${fmtE(qRevenue)}.`,
      };

      await supabase.from('governance_log').insert([{
        venue: v, year: y, month: lastMonth,
        category:   'board_decision',
        amount:     feeTotal,
        created_by: currentUser?.name || currentUser?.email || 'Admin',
        ...logEntry,
      }]);
    }

    load();
  };

  const isSassys = venue === 'Sassys';

  const annualChartData = MONTHS.map((m, i) => {
    const cy    = monthly.find(r => r.month === i+1);
    const ly    = monthlyLY.find(r => r.month === i+1);
    const cyRev = govDailyRev(dailyRev, i+1) || cy?.actual_revenue || null;
    const lyRev = govDailyRev(dailyRevLY, i+1) || ly?.actual_revenue || null;
    return {
      month:    m,
      Revenue:  cyRev,
      'Rev LY': lyRev,
      EBITDA:   cy ? Math.round(calcEBITDA(cy, isSassys)) : null,
    };
  });

  return (
    <div style={{ padding: isMobile ? '16px 12px' : '24px', maxWidth:1200, fontFamily:'inherit' }}>

      <div style={{ marginBottom:20 }}>
        <h2 style={{ margin:0, fontSize:22, fontWeight:700, color:C.charcoal }}>Governance</h2>
        <p style={{ margin:'2px 0 0', fontSize:13, color:C.mid }}>Quarterly reporting &amp; shareholder communications — {venue}</p>
      </div>

      {/* Venue + Year selectors */}
      <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:24, alignItems:'center' }}>
        {VENUES.map(v => (
          <button key={v} onClick={() => setVenue(v)} style={{ padding:'5px 14px', borderRadius:20, border:'none', cursor:'pointer', fontSize:13, fontWeight:500, background:venue===v?C.charcoal:'#f1f5f9', color:venue===v?C.white:C.mid, transition:'all 0.15s' }}>{v}</button>
        ))}
        <div style={{ width:1, background:C.border, margin:'0 4px', alignSelf:'stretch' }}/>
        {YEARS.map(y => (
          <button key={y} onClick={() => setYear(y)} style={{ padding:'5px 14px', borderRadius:20, border:'none', cursor:'pointer', fontSize:13, fontWeight:500, background:year===y?C.charcoal:'#f1f5f9', color:year===y?C.white:C.mid, transition:'all 0.15s' }}>{y}</button>
        ))}
      </div>

      {loading ? (
        <div style={{ textAlign:'center', padding:'60px', color:C.mid }}>Loading…</div>
      ) : (
        <>
          {/* Annual charts — stacked on mobile */}
          <div style={{ display:'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap:16, marginBottom:24 }}>
            <div style={{ background:C.white, borderRadius:12, border:`1px solid ${C.border}`, padding:'16px 20px', boxShadow:'0 1px 4px rgba(0,0,0,0.04)' }}>
              <div style={{ fontWeight:700, fontSize:13, color:C.charcoal, marginBottom:4 }}>Annual Revenue — {venue}</div>
              <div style={{ fontSize:11, color:C.mid, marginBottom:12, display:'flex', gap:12 }}>
                <span style={{ display:'inline-flex', alignItems:'center', gap:4 }}><span style={{ width:12, height:3, background:C.navy, display:'inline-block', borderRadius:2 }}/>{year}</span>
                <span style={{ display:'inline-flex', alignItems:'center', gap:4 }}><span style={{ width:12, height:3, background:C.mid, display:'inline-block', borderRadius:2 }}/>{year-1}</span>
              </div>
              <ResponsiveContainer width='100%' height={160}>
                <LineChart data={annualChartData}>
                  <CartesianGrid strokeDasharray='3 3' stroke='#f1f5f9'/>
                  <XAxis dataKey='month' tick={{ fontSize:10 }}/>
                  <YAxis tick={{ fontSize:10 }} tickFormatter={v => `€${(v/1000).toFixed(0)}k`} width={48}/>
                  <Tooltip formatter={v => v ? `€${Number(v).toLocaleString('en-US', {maximumFractionDigits:0})}` : '—'}/>
                  <Line type='monotone' dataKey='Revenue' stroke={C.navy} strokeWidth={2} dot={{ r:3 }}/>
                  <Line type='monotone' dataKey='Rev LY'  stroke={C.mid}  strokeWidth={1.5} strokeDasharray='4 2' dot={false} connectNulls/>
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div style={{ background:C.white, borderRadius:12, border:`1px solid ${C.border}`, padding:'16px 20px', boxShadow:'0 1px 4px rgba(0,0,0,0.04)' }}>
              <div style={{ fontWeight:700, fontSize:13, color:C.charcoal, marginBottom:12 }}>Monthly EBITDA — {venue}</div>
              <ResponsiveContainer width='100%' height={160}>
                <BarChart data={annualChartData}>
                  <CartesianGrid strokeDasharray='3 3' stroke='#f1f5f9'/>
                  <XAxis dataKey='month' tick={{ fontSize:10 }}/>
                  <YAxis tick={{ fontSize:10 }} tickFormatter={v => `€${(v/1000).toFixed(0)}k`} width={48}/>
                  <Tooltip formatter={v => v ? `€${Number(v).toLocaleString('en-US', {maximumFractionDigits:0})}` : '—'}/>
                  <Bar dataKey='EBITDA' fill={C.green} radius={[3,3,0,0]}/>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Quarter panels */}
          <div style={{ display:'flex', flexDirection:'column', gap:16, marginBottom:24 }}>
            {QUARTERS.map(q => (
              <QuarterPanel
                key={q.num}
                venue={venue}
                quarter={q}
                year={year}
                monthly={monthly}
                monthlyLY={monthlyLY}
                dailyRev={dailyRev}
                dailyRevLY={dailyRevLY}
                quarterRecord={quarterly.find(r => r.quarter === q.num)}
                onPublishToggle={handlePublishToggle}
                onIncentiveAction={handleIncentiveAction}
                publishing={publishing}
                currentUser={currentUser}
                onQuarterUpdate={load}
                isAdvisor={isAdvisor}
              />
            ))}
          </div>

          {/* FY Summary card */}
          {(() => {
            const isCurrentYear = year === CURRENT_YEAR;
            const sumAll  = (key) => monthly.reduce((a, r) => a + (r?.[key] || 0), 0);
            const sumAllLY= (key) => monthlyLY.reduce((a, r) => a + (r?.[key] || 0), 0);

            const fy = {
              revenue:      Array.from({length:12},(_,i)=>i+1).reduce((s,m)=>s+(govDailyRev(dailyRev,m)||monthly.find(r=>r.month===m)?.actual_revenue||0),0),
              food_cost:    sumAll('food_cost'),
              bev_cost:     sumAll('bev_cost'),
              oedipus:      sumAll('oedipus_rent') + sumAll('oedipus_utilities') + sumAll('oedipus_rev_share'),
              kit_payroll:  sumAll('kitchen_payroll'),
              foh_payroll:  sumAll('foh_payroll'),
              lm_total:     sumAll('lm_prep_payroll_alloc') + sumAll('lm_transfers_alloc'),
              group_costs:  sumAll('group_costs'),
              other_opex:   sumAll('other_opex'),
              net_profit:   sumAll('net_profit'),
              covers:       sumAll('actual_covers'),
              food_revenue: sumAll('food_revenue'),
              bev_revenue:  sumAll('bev_revenue'),
            };
            fy.payroll = fy.kit_payroll + (isSassys ? 0 : fy.foh_payroll);
            fy.ebitda  = fy.revenue - fy.food_cost
              - (isSassys ? 0 : fy.bev_cost) - (isSassys ? fy.oedipus : 0)
              - fy.payroll - fy.lm_total - fy.group_costs - fy.other_opex;
            fy.totalExpense = fy.revenue - fy.ebitda;

            const ly = {
              revenue:      Array.from({length:12},(_,i)=>i+1).reduce((s,m)=>s+(govDailyRev(dailyRevLY,m)||monthlyLY.find(r=>r.month===m)?.actual_revenue||0),0),
              food_cost:    sumAllLY('food_cost'),
              bev_cost:     sumAllLY('bev_cost'),
              oedipus:      sumAllLY('oedipus_rent') + sumAllLY('oedipus_utilities') + sumAllLY('oedipus_rev_share'),
              kit_payroll:  sumAllLY('kitchen_payroll'),
              foh_payroll:  sumAllLY('foh_payroll'),
              lm_total:     sumAllLY('lm_prep_payroll_alloc') + sumAllLY('lm_transfers_alloc'),
              group_costs:  sumAllLY('group_costs'),
              other_opex:   sumAllLY('other_opex'),
              net_profit:   sumAllLY('net_profit'),
              covers:       sumAllLY('actual_covers'),
              food_revenue: sumAllLY('food_revenue'),
              bev_revenue:  sumAllLY('bev_revenue'),
            };
            ly.payroll = ly.kit_payroll + (isSassys ? 0 : ly.foh_payroll);
            ly.ebitda  = ly.revenue - ly.food_cost
              - (isSassys ? 0 : ly.bev_cost) - (isSassys ? ly.oedipus : 0)
              - ly.payroll - ly.lm_total - ly.group_costs - ly.other_opex;
            ly.totalExpense = ly.revenue - ly.ebitda;

            if (!fy.revenue) return null;

            const monthsWithData = monthly.length;
            const ytdLabel = isCurrentYear && monthsWithData < 12
              ? `YTD (${MONTHS[monthly[monthly.length-1]?.month - 1]} ${year})`
              : `FY ${year}`;

            const FYRow = ({ label, cy, ly: lyVal, indent, bold, highlight, isRevenue, dimIfZero }) => {
              if (dimIfZero && (!cy || cy === 0)) return null;
              const vs    = cy && lyVal ? cy - lyVal : null;
              const vsPct = cy && lyVal ? ((cy - lyVal) / Math.abs(lyVal) * 100) : null;
              const good  = vs !== null && (isRevenue ? vs >= 0 : vs <= 0);
              return (
                <tr style={{ borderBottom:`1px solid ${C.border}`, background:highlight?C.yellowLight:'transparent' }}>
                  <td style={{ padding:'8px 16px', paddingLeft:indent?28:16, fontSize:13, fontWeight:bold?700:400, color:C.charcoal }}>{label}</td>
                  <td style={{ padding:'8px 16px', textAlign:'right', fontWeight:bold?800:600, fontSize:13, color:C.charcoal }}>{cy ? fmt(cy) : '—'}</td>
                  <td style={{ padding:'8px 16px', textAlign:'right', fontSize:12, color:C.mid }}>{lyVal ? fmt(lyVal) : '—'}</td>
                  <td style={{ padding:'8px 16px', textAlign:'right', fontSize:12 }}>
                    {vsPct !== null
                      ? <span style={{ fontWeight:700, color:good?C.green:C.red }}>{vsPct>=0?'+':''}{vsPct.toFixed(1)}%</span>
                      : '—'}
                  </td>
                </tr>
              );
            };

            return (
              <div style={{ background:C.white, borderRadius:12, border:`2px solid ${C.border}`, overflow:'hidden', boxShadow:'0 1px 6px rgba(0,0,0,0.06)', marginBottom:24 }}>
                {/* Header */}
                <div style={{ background:C.charcoal, padding:'14px 20px', display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:12 }}>
                  <div>
                    <div style={{ fontWeight:800, fontSize:15, color:C.white }}>{ytdLabel} — {venue}</div>
                    {isCurrentYear && monthsWithData < 12 && (
                      <div style={{ fontSize:11, color:'#94a3b8', marginTop:2 }}>{monthsWithData} months of data</div>
                    )}
                  </div>
                  <div style={{ display:'flex', gap:24 }}>
                    {[
                      { label:'Revenue',    value:fmt(fy.revenue),            color:C.white },
                      { label:'EBITDA',     value:fmt(Math.round(fy.ebitda)), color:fy.ebitda>=0?C.green:C.red },
                      ...(fy.net_profit ? [{ label:'Net Profit', value:fmt(fy.net_profit), color:fy.net_profit>=0?C.green:C.red }] : []),
                    ].map(k => (
                      <div key={k.label} style={{ textAlign:'center' }}>
                        <div style={{ fontSize:10, color:'rgba(255,255,255,0.5)', fontWeight:700, textTransform:'uppercase', marginBottom:3 }}>{k.label}</div>
                        <div style={{ fontSize:15, fontWeight:800, color:k.color }}>{k.value}</div>
                      </div>
                    ))}
                  </div>
                </div>

                <div style={{ padding:'0 0 20px' }}>
                  {/* P&L table */}
                  <div style={{ overflowX:'auto' }}>
                    <table style={{ width:'100%', borderCollapse:'collapse' }}>
                      <thead>
                        <tr style={{ background:C.cream, borderBottom:`1px solid ${C.border}` }}>
                          {['', 'Actual', String(year-1), 'vs LY %'].map((h, i) => (
                            <th key={i} style={{ padding:'9px 16px', textAlign:i===0?'left':'right', fontSize:10, fontWeight:700, color:C.mid, textTransform:'uppercase', letterSpacing:'0.04em' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        <FYRow label='Revenue'        cy={fy.revenue}     ly={ly.revenue}     bold isRevenue/>
                        <FYRow label='Food Cost'       cy={fy.food_cost}   ly={ly.food_cost}   indent/>
                        {!isSassys && <FYRow label='Bev Cost'      cy={fy.bev_cost}    ly={ly.bev_cost}    indent dimIfZero/>}
                        {isSassys  && <FYRow label='Oedipus Costs' cy={fy.oedipus}     ly={ly.oedipus}     indent dimIfZero/>}
                        <FYRow label='Payroll'         cy={fy.payroll}     ly={ly.payroll}     indent/>
                        {fy.lm_total > 0 && <FYRow label='LM Allocations' cy={fy.lm_total} ly={ly.lm_total>0?ly.lm_total:null} indent dimIfZero/>}
                        <FYRow label='Management Fees' cy={fy.group_costs} ly={ly.group_costs} indent dimIfZero/>
                        <FYRow label='Other OpEx'       cy={fy.other_opex}     ly={ly.other_opex}     indent dimIfZero/>
                        <FYRow label='Total Expenses'   cy={fy.totalExpense}   ly={ly.totalExpense}   bold/>
                        <FYRow label='EBITDA'           cy={fy.ebitda}         ly={ly.ebitda}         bold highlight isRevenue/>
                        {fy.net_profit !== 0 && <FYRow label='Net Profit' cy={fy.net_profit} ly={ly.net_profit} bold isRevenue/>}
                      </tbody>
                    </table>
                  </div>

                  {/* KPI strip */}
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(120px, 1fr))', gap:10, padding:'16px 20px 0' }}>
                    {[
                      { label:'Food Cost %', val:fmtPct(fy.food_cost, fy.food_revenue),   ly:fmtPct(ly.food_cost, ly.food_revenue) },
                      ...(!isSassys ? [{ label:'Bev Cost %', val:fmtPct(fy.bev_cost, fy.bev_revenue), ly:fmtPct(ly.bev_cost, ly.bev_revenue) }] : []),
                      ...(isSassys  ? [{ label:'Oedipus %',  val:fmtPct(fy.oedipus,  fy.revenue),     ly:fmtPct(ly.oedipus,  ly.revenue) }] : []),
                      { label:'Payroll %',  val:fmtPct(fy.payroll, fy.revenue),      ly:fmtPct(ly.payroll, ly.revenue) },
                      { label:'Covers',     val:fmtNum(fy.covers),                   ly:fmtNum(ly.covers) },
                      { label:'Avg Check',  val:fy.covers ? fmt(Math.round(fy.revenue/fy.covers)) : '—', ly:ly.covers ? fmt(Math.round(ly.revenue/ly.covers)) : '—' },
                    ].map(k => (
                      <div key={k.label} style={{ background:C.cream, borderRadius:8, padding:'10px 14px', border:`1px solid ${C.border}` }}>
                        <div style={{ fontSize:10, color:C.mid, textTransform:'uppercase', fontWeight:700, marginBottom:3 }}>{k.label}</div>
                        <div style={{ fontSize:15, fontWeight:800, color:C.charcoal }}>{k.val}</div>
                        {k.ly && k.ly !== '—' && <div style={{ fontSize:10, color:C.mid, marginTop:2 }}>LY: {k.ly}</div>}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Art. 9 */}
          <div style={{ background:C.cream, borderRadius:10, padding:'14px 18px', border:`1px solid ${C.border}` }}>
            <div style={{ fontSize:11, fontWeight:700, color:C.mid, textTransform:'uppercase', letterSpacing:'0.04em', marginBottom:6 }}>Article 9 — Dividend Policy</div>
            <div style={{ fontSize:12, color:C.mid, lineHeight:1.6 }}>
              The Company shall distribute at least 80% of net profit annually, provided solvency remains ≥ 30%
              post-distribution and cash reserves exceed €200,000. A 20% gross profit reserve for future investments
              applies before any distribution. Distributions require resolution of the General Meeting of Shareholders.
            </div>
          </div>
        </>
      )}
    </div>
  );
}